
import { defineComponent, ref, reactive, nextTick, watchEffect } from 'vue'
import type { FormInstance, FormRules, UploadInstance, UploadProps } from "element-plus";
import JwtService from "@/core/services/JwtService";
import { showNotification, errManager } from "@/core/config/Tools";
import GlobalService from '@/core/services/GlobalService';
import { actions } from "@/core/data/shared"
import { useI18n } from "vue-i18n/index";

export default defineComponent({
    name: "modal-team",
    props: {
        idregister: {
            type: null,
            required: false,
            default: 0
        },
        action: {
            type: null,
            required: true,
            default: 0
        },
        idevent: {
            type: String,
            required: true,
            default: 0
        },
        idsport: {
            type: String,
            required: true,
            default: 0
        },
        rowClub: {
            type: Object,
            required: false,
            default: {}
        }
    },
    emits: ["teamResponse"],
    setup(props, { emit }) {
        const { t } = useI18n();
        const loading = ref(false);
        const urlUpload = ref(`${process.env.VUE_APP_API_URL}/api/teams/save`);
        const headers = ref({
            Authorization: `Bearer ${JwtService.getToken()}`,
            'X-Company': JwtService.getCompany()
        });
        const titleDialog = ref('');
        const isModalVisible = ref(false);
        const form = reactive<any>({});
        const ruleFormRef = ref<FormInstance>();
        const rules = reactive<FormRules>({});
        const uploadRef = ref<UploadInstance>();
        const fileList = ref<File[]>([]);
        const extens = ref(".png, .jpg, jpeg");
        const allowedMIMEs = ref(['image/png', 'image/jpg', 'image/jpeg']);
        const maxFileSizeMB = 1;
        const maxFileSizeInBytes = maxFileSizeMB * 1024 * 1024;
        const previewUrl = ref<string | null>(null);
        const configInit = ref<any>({});

        const openModal = () => {
            isModalVisible.value = true;
        };

        const onOpen = () => {
            form.idevent = props.idevent;
            form.idsport = props.idsport;
            form.idinstitution = props.rowClub?.idinstitution
            titleDialog.value = props.action == 1 ? t('registerTeam') : t('editTeam');
        };

        const onOpened = () => {
            getConfigInit();
            if (props.idregister && props.action == 2) {
                getTeam();
            }

        }

        const onClosed = () => {
            resetForm(ruleFormRef.value);
            Object.keys(form).forEach(key => delete form[key]);
            removeFile();
        };

        const resetForm = (formEl: FormInstance | undefined) => {
            if (!formEl) return;
            formEl.resetFields();
        };

        const handleChange = (file: any) => {
            if (!allowedMIMEs.value.includes(file.raw.type)) {
                showNotification({ title: process.env.VUE_APP_NAME, message: 'Formato no permitido', status: 'error' });
                return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(file.raw);
            reader.onload = () => {
                previewUrl.value = reader.result as string;
            };

            fileList.value = [file.raw]; // Mantener solo un archivo
        };

        const removeFile = () => {
            fileList.value = [];
            previewUrl.value = null;
            if (props.action == 2) {
                form.avatar = null;
            }
        };

        const beforeUpload: UploadProps['beforeUpload'] = (rawFile) => {
            if (!allowedMIMEs.value.includes(rawFile.type)) {
                showNotification({ title: 'Error', message: '¡El archivo no tiene el formato permitido!', status: 'error' })
                return Promise.resolve(false);
            } else if (rawFile.size > maxFileSizeInBytes) {
                showNotification({ title: 'Error', message: `El archivo supera el tamaño máximo permitido de ${maxFileSizeMB} MB.`, status: 'error' })
                return Promise.resolve(false);
            }
            return Promise.resolve(true);
        };

        const isJSON = (str: string): boolean => {
            try {
                JSON.parse(str);
                return true;
            } catch {
                return false;
            }
        };

        const handleError: UploadProps['onError'] = (error: Error, uploadFile) => {
            setTimeout(() => {
                let errorMessage = 'Error desconocido.';
                if (isJSON(error.message)) {
                    const errorData = JSON.parse(error.message);
                    errorMessage = errorData.message || errorMessage;
                } else {
                    errorMessage = error.message || errorMessage;
                }
                removeFile()
                showNotification({ title: "Error", message: errorMessage, status: "error" })
            }, 100);
        };

        const handleSuccess: UploadProps['onSuccess'] = async (response, uploadFile) => {
            showNotification(response)
            if (response.status == 'success') {
                await nextTick();  // Espera a que Vue termine el ciclo de renderizado
                isModalVisible.value = false;
                emit("teamResponse", response)
            }
        };

        const validateAndSubmit = async () => {
            if (!ruleFormRef.value) return;

            await ruleFormRef.value.validate((valid) => {
                if (!valid) {
                    showNotification({ title: process.env.VUE_APP_NAME, message: 'Completa todos los campos requeridos.', status: 'error' });
                    return;
                }

                /*
                if (fileList.value.length === 0) {
                    showNotification({title: process.env.VUE_APP_NAME, message: 'Debes seleccionar una imagen.', status: 'error'});
                    return;
                }
                */

                if (!uploadRef.value || fileList.value.length == 0) {
                    saveManuality();
                } else {
                    uploadRef.value?.submit();
                }
            });
        };

        const saveManuality = async () => {
            //save || update
            await GlobalService.post(`teams/${actions[props.action].method}`, form)
                .then((res) => {
                    showNotification({ title: res.data.title, message: res.data.message, status: res.data.status });
                    if (res.data.status == 'success') {
                        isModalVisible.value = false;
                        emit("teamResponse", res.data)
                    }
                }).catch(({ response }) => {
                    loading.value = false
                    errManager(response)
                })
        }

        const getConfigInit = async () => {
            loading.value = true;
            await GlobalService.get(`api/teams/${props.idevent}/${props.idsport}`, 'setting')
                .then((res) => {
                    console.log(res.data);
                    loading.value = false;
                    configInit.value = res.data;
                    if (res.data.mstatus == 'O') {
                        if (res.data?.definition_teams == '1') {
                            form.name = res.data.name;
                            form.alias = res.data.alias;
                        }
                    }
                }).catch(({ response }) => {
                    loading.value = false;
                    errManager(response)
                })
        }

        const getTeam = async () => {
            loading.value = true;
            await GlobalService.get(`api/teams/${props.idregister}`, 'edit')
                .then((res) => {
                    loading.value = false;
                    if (res.data) {
                        previewUrl.value = `${process.env.VUE_APP_REPO}/${res.data.avatar}`
                        Object.assign(form, res.data)
                    }
                }).catch(({ response }) => {
                    loading.value = false
                    errManager(response)
                })
        }

        return {
            t,
            loading,
            headers,
            urlUpload,
            uploadRef,
            titleDialog,
            isModalVisible,
            form,
            ruleFormRef,
            rules,
            fileList,
            extens,
            allowedMIMEs,
            previewUrl,
            actions,
            configInit,

            // Métodos
            openModal,
            onOpen,
            onOpened,
            onClosed,
            handleChange,
            removeFile,
            beforeUpload,
            handleError,
            handleSuccess,
            validateAndSubmit,
            getTeam
        };
    },
});
